/**
 * Spectrum Quick-Add
 *
 * Binds every `[data-spectrum-quick-add-trigger]` button on the page to the
 * single `<dialog id="spectrum-quick-add-dialog">` rendered from
 * `layout/theme.liquid`. On click, fetches the product JSON via Shopify's
 * Ajax API, renders option rows and the active variant's price, and on
 * Add-to-Cart submits to `/cart/add.js` and dispatches `cart:update` so the
 * theme's cart drawer auto-opens.
 *
 * Also handles `[data-spectrum-direct-atc]` buttons for single-variant
 * products (no modal — direct ATC, cart drawer opens).
 *
 * The picker is NATIVE: it builds its own option rows from `/products/<handle>.js`
 * plus the `spectrum.value_display` metafield (authored swatch images + badges) and
 * a small measured renderer map (AXIS_RENDERERS), emitting the PDP snippets' own
 * `sai-*` class names so styling comes from the shared compiled stylesheet. It does
 * NOT borrow the PDP's Studio picker instances — an earlier `?view=spectrum-qa-picker`
 * path did, via a hand-written suffix -> `_sai-<handle>` map that went stale on every
 * republish; it has been removed.
 */

const DIALOG_ID = "spectrum-quick-add-dialog";
const CART_UPDATE_EVENT = "cart:update";
const CART_ERROR_EVENT = "cart:error";

const COLOR_HEX = (name) => {
  const v = String(name || "").toLowerCase();
  if (/(black|onyx|jet|charcoal)/.test(v)) return "#1a1a1a";
  if (/(white|ivory|cream|pearl)/.test(v)) return "#f3f1ec";
  if (/(yellow|mustard|gold)/.test(v)) return "#ffd400";
  if (/(orange|rust|amber)/.test(v)) return "#f57c00";
  if (/(navy|royal)/.test(v)) return "#1f2c5c";
  if (/aqua/.test(v)) return "#3aa3a3";
  if (/(sky|powder|baby blue)/.test(v)) return "#9bc4e2";
  if (/(blue|cobalt)/.test(v)) return "#1976d2";
  if (/(red|crimson|scarlet)/.test(v)) return "#d32f2f";
  if (/(pink|rose|blush)/.test(v)) return "#ec407a";
  if (/(maroon|wine|burgundy)/.test(v)) return "#7a1f2b";
  if (/(purple|violet|plum)/.test(v)) return "#7b1fa2";
  if (/olive/.test(v)) return "#6b6c2e";
  if (/sage/.test(v)) return "#88a37b";
  if (/green/.test(v)) return "#2e7d32";
  if (/(teal|mint)/.test(v)) return "#26a69a";
  if (/(brown|tan|caramel|chocolate|mocha)/.test(v)) return "#6d4c41";
  if (/(beige|sand|nude)/.test(v)) return "#d7c7a1";
  if (/(silver|platinum)/.test(v)) return "#c0c0c0";
  if (/(grey|gray|graphite)/.test(v)) return "#7a7a7a";
  return "#cfcfcf";
};

const isColorOption = (name) => /^colou?r$/i.test(String(name || "").trim());

/**
 * Size axes, for the unit toggle and size-specific styling. Catalogue audit found
 * these spellings in use: "Size", "Shoe Size", "Shoe size", "Size (UK Size)",
 * "Size (Left+Right)".
 */
const isSizeOption = (name) =>
  /(^|\s)size(\s|$|\s*\()/i.test(String(name || "").trim());

/**
 * Shopify's synthetic axis for a product with no real options ("Title" whose only
 * value is "Default Title"). The PDP never renders it, so neither do we.
 *
 * Note what is deliberately NOT hidden: a single-value REAL axis. Dropping those
 * looked harmless — one value cannot change the variant — but the PDP renders them,
 * and on a product whose only axis is single-valued it left the mini-PDP completely
 * empty (frido-ultimate-pro-seat-cushion: one option, Color = ["Premium Grey"]).
 * It also caused the only remaining renderer mismatches in the parity sweep
 * (tt-frido-ultimate-deep-sleep-pillow "Cover Options", frido-kids-socks "Length").
 *
 * Name-based hiding is likewise avoided: a catalogue audit showed the obvious
 * candidates are REAL axes — "Category" (4 values on frido-topper's 81 variants)
 * and "Version" (frido-vajra-gaming-chair) — and hiding either would make most of
 * those products' variants unselectable.
 */
/**
 * Size-label helpers, copied VERBATIM from `assets/sai-snippet-fszfrd01.js`
 * (DIM_RE 39, UK_RE 40, detectLayout 42, convertDim 50, convertUk 61).
 *
 * These are pure — string + unit in, string out, no DOM and no Studio props — so
 * copying removes the entire "units/sizing bugs" risk that reimplementing them
 * would carry. Do not "tidy" them: any divergence shows up as a size label that
 * disagrees with the PDP.
 */
const DIM_RE =
  /^\s*\d+(?:\.\d+)?\s*(?:["”]|\s*(?:in|inch|inches))?\s*[xX×]\s*\d+(?:\.\d+)?\s*(?:["”]|\s*(?:in|inch|inches))?\s*$/;
const UK_RE = /\bUK\b/i;

const detectSizeLayout = (values) => {
  if (values.length && values.every((v) => DIM_RE.test(v))) return "dimensions";
  if (values.length && values.every((v) => UK_RE.test(v))) return "uk";
  return "pills";
};

// "72\" x 36\"" -> converted dimension label per unit
const convertDim = (label, unit) => {
  if (unit === "inch") return label;
  const nums = String(label).match(/\d+(?:\.\d+)?/g);
  if (!nums || !nums.length) return label;
  const factor = unit === "feet" ? 1 / 12 : 2.54;
  const sym = unit === "feet" ? "ft" : "cm";
  const conv = nums.map((n) =>
    (Math.round(Number.parseFloat(n) * factor * 10) / 10).toString()
  );
  return conv.join(" x ") + " " + sym;
};

// "9 UK" -> foot-length via barleycorn approximation ((size + 23) / 3 inches)
const convertUk = (label, unit) => {
  if (unit === "inch") return label;
  const m = String(label).match(/(\d+(?:\.\d+)?)/);
  if (!m) return label;
  const inches = (Number.parseFloat(m[1]) + 23) / 3;
  if (unit === "feet") return Math.round((inches / 12) * 100) / 100 + " ft";
  return Math.round(inches * 2.54 * 10) / 10 + " cm";
};

/**
 * fszfrd01 reads its starting unit from a Studio prop; a native picker has no
 * instance to read, so this is the snippet's own fallback
 * (`props.default_unit || 'inch'`) hard-coded — the PDP therefore opens on the
 * same unit unless a specific instance was overridden.
 */
const DEFAULT_SIZE_UNIT = "inch";
const UNIT_LABELS = [
  ["inch", "Inches"],
  ["feet", "Feet"],
  ["cm", "CM"],
];

const isDefaultTitleOption = (opt) => {
  const name = String(opt?.name || "").trim().toLowerCase();
  const values = opt?.values || [];
  if (name === "title" && values.length <= 1) return true;
  return (
    values.length === 1 &&
    String(values[0] || "").trim().toLowerCase() === "default title"
  );
};

/**
 * Axes that render as a Men/Women switch INSIDE the size picker's header rather
 * than as a row of pills of their own — see `sai-snippet-fszfrd01.js` `showGender`.
 *
 * The PDP binds this through its Studio `gender_axis` prop and deliberately does
 * NOT name-sniff ("a wrong name renders nothing — fail visible"). A native picker
 * has no instance to read that prop from, so it becomes a theme-side list, the same
 * treatment `default_unit` got. The catalogue audit found exactly one name in use:
 * `Gender`, on the 3 frido-barefoot products.
 */
const GENDER_AXIS_NAMES = ["gender"];
const isGenderOption = (name) =>
  GENDER_AXIS_NAMES.includes(String(name || "").trim().toLowerCase());

/**
 * Which renderer an axis gets, by option name.
 *
 * The PDP assigns these through per-instance Studio `axis` props, so there is no
 * signal in the product data to infer them from. An earlier attempt DID infer
 * card axes ("has authored images AND varying prices") and got maternity wrong:
 * `Variant` satisfies both yet the PDP renders it with ftyfrd01, not fcbfrd02.
 * Verified by reading both PDPs — topper renders Category/Cover Options and
 * maternity Variant/Bundle with exactly the same pair of renderers:
 *
 *   GROUP -> sai-ftyfrd01 `__cards`  : 2-col grid of plain wide cards, no price
 *   CARD  -> sai-fcbfrd02 `__rows`   : full-width rows, thumb + SAVE/MRP/price
 *
 * Explicit lists match how the PDP itself works ("no name sniffing — a wrong name
 * renders nothing, fail visible"). An unlisted axis falls through to plain pills,
 * which is a safe default rather than a broken one.
 */
/**
 * Which PDP renderer each axis gets, keyed `<template>||<lowercased option name>`.
 *
 * MEASURED, NOT GUESSED. Generated by reading the live PDP of all 156 openable
 * multi-variant products and recording which `sai-*` element renders each axis.
 * Three earlier attempts to infer this from product data all failed, because the
 * choice lives in per-instance Studio config and leaves no trace in the data:
 *
 *   "cover options" -> fcbfrd02 cards  on frido-topper
 *                   -> fswfrd01 tiles  on frido-cover-cards
 *                   -> ftyfrd01 group  on frido
 *
 * Same axis name, three renderers. Template disambiguates all of them: the sweep
 * found ZERO cases of one template rendering the same axis name two ways, so this
 * key is complete for the current catalogue.
 *
 * Regenerate after adding a template or repointing a picker instance — an unmapped
 * axis falls back to the heuristics in rendererFor(), which are reasonable but not
 * guaranteed to match.
 */
const AXIS_RENDERERS = {
  "frido-barefoot||color": "swatch",
  "frido-barefoot||size": "size",
  "frido-belt||colour": "swatch",
  "frido-belt||size": "size",
  "frido-chairs||color": "swatch",
  "frido-combo||color": "swatch",
  "frido-cover-cards||cover options": "swatch",
  "frido-cover-cards||cover type": "swatch",
  "frido-cover-cards||size": "size",
  "frido-footwear-pills||color": "swatch",
  "frido-footwear-pills||size": "size",
  "frido-footwear||color": "swatch",
  "frido-footwear||size": "size",
  "frido-kids||color": "swatch",
  "frido-maternity||bundle": "card",
  "frido-maternity||color": "swatch",
  "frido-maternity||variant": "group",
  "frido-nasal||pack": "card",
  "frido-nasal||quantity": "card",
  "frido-size-first||color": "swatch",
  "frido-size-first||hand": "group",
  "frido-size-first||size": "size",
  "frido-socks||color": "swatch",
  "frido-socks||size": "size",
  "frido-topper||category": "group",
  "frido-topper||cover options": "card",
  "frido-topper||size": "size",
  "frido-wedge-neck||color": "swatch",
  "frido||body weight": "group",
  "frido||category": "group",
  "frido||color": "swatch",
  "frido||colour": "swatch",
  "frido||cover options": "group",
  "frido||gender": "group",
  "frido||hand": "group",
  "frido||length": "group",
  "frido||pack of": "card",
  "frido||size": "size",
  "frido||variant": "group",
  "frido||weight carrying capacity": "group",
};

const GROUP_AXIS_NAMES = ["category", "variant", "version"];
// NOT "cover type": frido-latex-pillow renders that axis with fswfrd01 image tiles,
// while frido-topper's similarly-named "Cover Options" is fcbfrd02 card rows. Both
// verified by reading the live PDPs — the names are near-synonyms but the renderers
// differ, which is why this list is evidence-driven rather than guessed from wording.
const CARD_AXIS_NAMES = ["cover options", "bundle", "pack", "pack of", "quantity"];
const isGroupOption = (name) =>
  GROUP_AXIS_NAMES.includes(String(name || "").trim().toLowerCase());
const isCardAxisName = (name) =>
  CARD_AXIS_NAMES.includes(String(name || "").trim().toLowerCase());

const formatMoney = (minorUnits, currency) => {
  const amount = Number(minorUnits) / 100;
  const cur = currency || window.Shopify?.currency?.active || "INR";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (_e) {
    return `${cur} ${amount.toFixed(0)}`;
  }
};

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/**
 * Build [{ name, position, values: [...] }] from Shopify Ajax product JSON.
 * `/products/{handle}.js` returns `options` as a string array — values need
 * to be derived from variants.
 */
const buildOptionsWithValues = (product) => {
  const optionsRaw = product.options || [];
  return optionsRaw.map((opt, idx) => {
    const position = idx + 1;
    // /products/{handle}.js may return options as string array OR object array
    // (e.g. [{name: "Size", values: [...]}]). Defend against both.
    const name =
      typeof opt === "string"
        ? opt
        : opt && typeof opt.name === "string"
          ? opt.name
          : `Option ${position}`;
    const seen = new Set();
    const values = [];
    for (const v of product.variants || []) {
      const val = v[`option${position}`];
      if (val != null && !seen.has(val)) {
        seen.add(val);
        values.push(val);
      }
    }
    return { name, position, values };
  });
};

class SpectrumQuickAdd {
  constructor() {
    this.dialog = document.getElementById(DIALOG_ID);
    if (!this.dialog) return;

    this.cache = new Map();
    this.currentProduct = null;
    this.currentOptions = []; // [{ name, position, values }]
    this.selected = []; // selected option values, one per position
    this.atcInFlight = false;

    this.titleEl = this.dialog.querySelector("[data-spectrum-qa-title]");
    this.thumbEl = this.dialog.querySelector("[data-spectrum-qa-thumb]");
    this.optionsEl = this.dialog.querySelector("[data-spectrum-qa-options]");

    // Clicking the header thumbnail or the title navigates to the product's PDP
    // (with the selected variant when one is chosen). Bound once — the elements
    // persist across opens, so the handler reads the current product at click time.
    var self = this;
    function goToProduct() {
      var p = self.currentProduct;
      if (!p) return;
      var url = p.url || (p.handle ? "/products/" + p.handle : "");
      if (!url) return;
      var v = self.resolveVariant();
      if (v && v.id) url += (url.indexOf("?") === -1 ? "?" : "&") + "variant=" + v.id;
      window.location.href = url;
    }
    [this.titleEl, this.thumbEl].forEach(function (el) {
      if (!el) return;
      el.style.cursor = "pointer";
      el.addEventListener("click", goToProduct);
    });
    // Price row lives in the header (single slot).
    this.priceEls = Array.from(this.dialog.querySelectorAll("[data-spectrum-qa-prices]"));
    this.atcBtn = this.dialog.querySelector("[data-spectrum-qa-atc]");

    this.bindGlobalTriggers();
    this.bindDialog();
  }

  bindGlobalTriggers() {
    document.addEventListener("click", (e) => {
      const quickAddTrigger = e.target.closest(
        "[data-spectrum-quick-add-trigger]"
      );
      if (quickAddTrigger) {
        e.preventDefault();
        const handle = quickAddTrigger.dataset.spectrumQuickAddTrigger;
        // Native option-value swatch images (the same set the PDP renders) are
        // emitted on the trigger — pass them so the mini-PDP swatch matches the PDP.
        let swatchMap = null;
        if (quickAddTrigger.dataset.spectrumSwatches) {
          try { swatchMap = JSON.parse(quickAddTrigger.dataset.spectrumSwatches); } catch (e) {}
        }
        this.open(handle, swatchMap);
        return;
      }

      const directAtc = e.target.closest("[data-spectrum-direct-atc]");
      if (directAtc) {
        e.preventDefault();
        const variantId = directAtc.dataset.variantId;
        if (!variantId) return;
        const quantity = Math.max(1, parseInt(directAtc.dataset.quantity, 10) || 1);
        this.directAtc(directAtc, variantId, quantity);
      }
    });

    // Prefetch the product JSON the first time a user actually commits to
    // interacting with an ATC trigger (finger down or mouse down). This fires
    // ~50–150ms before the click event, giving the fetch a head start without
    // burning requests on every hover/scroll-past. The skeleton state covers
    // the remaining gap on click.
    const prefetched = new WeakSet();
    const onIntent = (e) => {
      const t = e.target.closest("[data-spectrum-quick-add-trigger]");
      if (!t || prefetched.has(t)) return;
      prefetched.add(t);
      const handle = t.dataset.spectrumQuickAddTrigger;
      if (handle) {
        this.fetchProduct(handle).catch(() => {});
        // Warm the per-value display data too, off the same intent signal, so the
        // open does no network at all.
        this.fetchValueDisplay(handle).catch(() => {});
      }
    };
    document.addEventListener("pointerdown", onIntent, { passive: true });
    document.addEventListener("touchstart", onIntent, { passive: true });
  }

  bindDialog() {
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) {
        this.close();
        return;
      }
      if (e.target.closest("[data-spectrum-qa-close]")) {
        this.close();
        return;
      }
      // Unit toggle (Inches / Feet / CM) — relabels sizes, changes no selection.
      const unitBtn = e.target.closest("[data-qa-unit]");
      if (unitBtn) {
        this.setSizeUnit(unitBtn.dataset.qaUnit);
        return;
      }
      // Gender switch: either label picks that value, the pill flips to the other.
      const genderLbl = e.target.closest("[data-qa-gender]");
      if (genderLbl) {
        this.setGender(genderLbl.dataset.qaGender);
        return;
      }
      if (e.target.closest("[data-qa-gender-toggle]")) {
        const opts = (this.currentOptions || []).filter((o) => !isDefaultTitleOption(o));
        const opt = this.hostedGenderAxis(opts);
        if (opt) {
          const [a, b] = opt.values;
          this.setGender(this.selected[opt.position - 1] === b ? a : b);
        }
        return;
      }
      const swatch = e.target.closest("[data-option-position]");
      // OOS (aria-disabled) options stay selectable — picking one selects its
      // out-of-stock variant so the footer shows Sold out (matches the PDP + live).
      if (swatch) {
        const pos = Number(swatch.dataset.optionPosition);
        const val = swatch.dataset.optionValue;
        const idx = pos - 1;
        if (this.selected[idx] !== val) {
          this.selected[idx] = val;
          // Changing an earlier axis can invalidate later ones (Single -> Queen
          // leaves Size on a Single-only dimension), which would resolve to no
          // variant at all. Snap them to something that exists first.
          this.reconcileLaterAxes(pos);
          this.renderOptions();
          this.renderFooter();
          this.updateThumb();
        }
        return;
      }
      if (e.target.closest("[data-spectrum-qa-atc]")) {
        this.modalAtc();
        return;
      }
    });

    this.dialog.addEventListener("cancel", (e) => {
      // native Escape handling
      e.preventDefault();
      this.close();
    });
  }

  async open(handle, swatchMap) {
    if (!handle) return;
    // Colour-value -> swatch-image map from the trigger (null for programmatic
    // opens); preferred over the variant photo when rendering colour swatches.
    this.swatchMap = swatchMap || null;
    const reopening = this.dialog.open;
    // Nothing clears the sheet between products, and open() paints it before the
    // fetch resolves — so opening product B showed product A's title, price and
    // options until B arrived. Blank it the moment we know the handle differs.
    //
    // The 160ms deferral below is kept ONLY for reopening the product already
    // loaded, where the stale content IS the correct content: deferring avoids a
    // skeleton blink on a cached reopen. Switching products can never take that
    // shortcut, however fast the fetch.
    const sameProduct = this.currentProduct && this.currentProduct.handle === handle;
    let skeletonTimer = null;
    if (sameProduct) {
      skeletonTimer = window.setTimeout(() => { this.dialog.dataset.loading = "true"; }, 160);
    } else {
      this.dialog.dataset.loading = "true";
      // Drop the stale product outright, so anything that reads it mid-open (a
      // stray render, the footer) can't resurrect the previous product's data.
      this.currentProduct = null;
      this.currentOptions = [];
      this.selected = [];
      if (this.optionsEl) this.optionsEl.innerHTML = "";
    }
    // showModal auto-focuses the close button; with html{scroll-behavior:smooth}
    // the browser scrolls it into view and jumps the page to the top. Pin the
    // page scroll across showModal (scroll-behavior:auto makes the re-pin instant
    // so there's no visible jump), then restore the original scroll behaviour.
    // Lock the page behind the sheet. showModal() blocks interaction but NOT
    // scrolling, so without this the collection behind the modal scrolls under
    // the shopper's finger — very visible now the sheet is tall enough to
    // scroll internally. Same approach as frido-mini-pdp.js.
    // Skipped when swapping product inside an already-open sheet: showModal()
    // throws on an open dialog, and the lock/scroll pinning is already applied.
    if (!reopening) {
      this.prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const pageScrollY = window.scrollY;
      const prevScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      if (typeof this.dialog.showModal === "function") {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute("open", "");
      }
      window.scrollTo(0, pageScrollY);
      document.documentElement.style.scrollBehavior = prevScrollBehavior;
    }
    // Add .is-open on the next painted frame so the slide/fade-in transition runs.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.dialog.classList.add("is-open"))
    );

    try {
      // Two independent requests, fired together: the product JSON and the
      // per-value display data (authored swatch images + merchandising badges +
      // template) that /products/{handle}.js cannot return. Sequentially they
      // would cost the sum of both round trips.
      const [product, display] = await Promise.all([
        this.fetchProduct(handle),
        this.fetchValueDisplay(handle),
      ]);
      window.clearTimeout(skeletonTimer);
      if (!product) {
        this.close();
        return;
      }
      this.currentProduct = product;
      this.currentOptions = buildOptionsWithValues(product);
      // Display data is decoration only — a failed/absent fetch degrades to variant
      // photos and the rendererFor() heuristics, never to a broken picker.
      this.valueDisplay = display?.values || {};
      this.currentTemplate = display?.template || "";
      // Pre-order CTA: the footer button (JS-built) relabels to "Pre-Order Now"
      // for pre-order variants, matching the PDP and the PLP card. LABEL ONLY —
      // the sheet shows no dispatch callout. The set is variant ids that are both
      // pre-order and available (an OOS variant reads "Sold out" instead), keyed
      // as strings so the resolveVariant() id compares cleanly.
      this.preorderLabel = display?.preorder_label || "Pre-Order Now";
      this.preorderIds = new Set(
        (display?.preorder_variant_ids || []).map((id) => String(id))
      );
      // Per-product, like the PDP: each instance starts on its default unit.
      this.sizeUnit = DEFAULT_SIZE_UNIT;

      const firstAvailable =
        (product.variants || []).find((v) => v.available) ||
        product.variants?.[0];
      this.selected = this.currentOptions.map((opt) =>
        firstAvailable ? firstAvailable[`option${opt.position}`] : null
      );

      this.renderTitle();
      this.renderOptions();
      this.renderFooter();
      this.updateThumb();
    } catch (err) {
      console.error("[spectrum-quick-add] open failed", err);
      this.close();
    } finally {
      window.clearTimeout(skeletonTimer);
      this.dialog.dataset.loading = "false";
    }
  }

  close() {
    document.body.style.overflow = this.prevBodyOverflow || "";
    this.prevBodyOverflow = null;

    const dlg = this.dialog;
    const finish = () => {
      if (typeof dlg.close === "function") {
        try { dlg.close(); } catch (_e) { dlg.removeAttribute("open"); }
      } else {
        dlg.removeAttribute("open");
      }
      // Clear the in-flight CTA state. `atcInFlight` is reset in modalAtc's
      // finally, but renderFooter() never runs again after a successful add, so
      // the button kept its loading state until the sheet was next opened —
      // which is why "Adding…" lingered on screen. Reset AFTER the exit
      // transition so the label doesn't flip back mid-slide.
      this.atcInFlight = false;
      if (this.atcBtn) {
        this.atcBtn.classList.remove("is-adding");
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = "Add to Cart";
      }
    };
    // Play the exit transition (remove .is-open) before actually closing.
    if (dlg.classList.contains("is-open")) {
      dlg.classList.remove("is-open");
      setTimeout(finish, 340);
    } else {
      finish();
    }
  }

  fetchProduct(handle) {
    if (!this.cache.has(handle)) {
      const promise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) throw new Error(`product fetch failed: ${res.status}`);
          return res.json();
        })
        .catch((err) => {
          this.cache.delete(handle);
          throw err;
        });
      this.cache.set(handle, promise);
    }
    return this.cache.get(handle);
  }

  renderTitle() {
    if (!this.titleEl) return;
    this.titleEl.textContent = this.currentProduct.title || "";
  }

  /**
   * Show the selected variant's first image in the header thumbnail. Falls back
   * to the product's featured image, then its first image. `/products/{handle}.js`
   * exposes `variant.featured_image` as an object with `.src` (or a bare string),
   * `product.featured_image` as a string, and `product.images` as a string array.
   */
  updateThumb() {
    if (!this.thumbEl) return;
    const variant = this.resolveVariant();
    const variantImg =
      variant &&
      (variant.featured_image?.src ||
        (typeof variant.featured_image === "string"
          ? variant.featured_image
          : null));
    const src =
      variantImg ||
      this.currentProduct?.featured_image ||
      this.currentProduct?.images?.[0] ||
      null;
    if (!src) {
      this.thumbEl.innerHTML = "";
      return;
    }
    const sep = src.includes("?") ? "&" : "?";
    this.thumbEl.innerHTML = `<img class="spectrum-qa-dialog__thumb-img" src="${escapeHtml(
      src
    )}${sep}width=300" alt="${escapeHtml(
      this.currentProduct?.title || ""
    )}" loading="lazy">`;
  }

  /**
   * Per-value display data (authored swatch image + badges) from the theme-owned
   * `?view=spectrum-qa-data`. Returns a `{ value: {image, badges, subtitle} }` map,
   * or {} on any failure — the picker must still render without it.
   */
  async fetchValueDisplay(handle) {
    if (!this.displayCache) this.displayCache = new Map();
    if (this.displayCache.has(handle)) return this.displayCache.get(handle);
    let data = {
      values: {},
      template: "",
      preorder_label: "Pre-Order Now",
      preorder_variant_ids: [],
    };
    try {
      // Deliberately NO `Accept: application/json` header. Shopify honours it over
      // the `view` param and serves its own product JSON ({"product":{...}}) instead
      // of this template, which silently yields no display data at all.
      const res = await fetch(
        `${window.Shopify?.routes?.root || "/"}products/${handle}?view=spectrum-qa-data`
      );
      if (res.ok) {
        const json = await res.json();
        data = {
          values: json.values || {},
          template: json.template || "",
          preorder_label: json.preorder_label || "Pre-Order Now",
          preorder_variant_ids: json.preorder_variant_ids || [],
        };
      }
    } catch (_e) {
      // Decoration only — swallow and fall back to variant photos.
    }
    this.displayCache.set(handle, data);
    return data;
  }

  /** Authored display entry for an option value, if any. */
  displayFor(value) {
    return (this.valueDisplay && this.valueDisplay[value]) || null;
  }

  renderOptions() {
    if (!this.optionsEl) return;
    // Filter for DISPLAY only — never for matching. `this.selected` is indexed by
    // `opt.position - 1` (see renderOptionValue), so dropping an entry from
    // currentOptions would shift every later axis into the wrong slot and send the
    // wrong variant to cart. Degenerate axes keep their slot and their single value.
    const visible = (this.currentOptions || []).filter(
      (opt) => !isDefaultTitleOption(opt)
    );
    // The gender axis is drawn as a switch in the size row's header (PDP parity),
    // so drop its own row — but ONLY once we know a size row exists to host it.
    // Without that guard a product with Gender and no Size would lose the axis
    // entirely and become unbuyable.
    const hosted = this.hostedGenderAxis(visible);
    const renderable = hosted ? visible.filter((o) => o !== hosted) : visible;
    if (renderable.length === 0) {
      this.optionsEl.innerHTML = "";
      return;
    }
    this.optionsEl.innerHTML = renderable
      .map((opt) => this.renderOptionRow(opt))
      .join("");
  }

  /**
   * The gender axis, if it should be folded into the size picker's header.
   * Mirrors fszfrd01's `showGender`: a gender axis with exactly two values.
   * Returns null when there is no size row to host it, so the caller falls back
   * to rendering gender as an ordinary axis.
   */
  hostedGenderAxis(visible) {
    const opts = visible || [];
    if (!opts.some((o) => isSizeOption(o.name))) return null;
    return (
      opts.find((o) => {
        if (!isGenderOption(o.name) || o.values.length !== 2) return false;
        // Only fold gender in when the PDP does NOT give it a picker of its own.
        // frido-barefoot has no Gender entry in AXIS_RENDERERS precisely because
        // its switch lives inside fszfrd01 — whereas `frido||gender` IS mapped
        // (to a group picker) and must keep its own row.
        const key = `${this.currentTemplate || ""}||${String(o.name).trim().toLowerCase()}`;
        return !AXIS_RENDERERS[key];
      }) || null
    );
  }

  /**
   * Values of `opt` that can still exist given the axes chosen BEFORE it.
   *
   * Scoped to preceding positions only, which is what the PDP does. Checking every
   * axis instead would let a stale later selection invalidate an earlier one — on
   * frido-topper that struck out Double/Queen/King simply because the currently
   * selected Size existed only for Single. Verified against the PDP: for Category
   * "Single" it offers 8 sizes (of 27) and disables nothing.
   */
  applicableValues(opt) {
    const pos = opt.position;
    const variants = this.currentProduct?.variants || [];
    return opt.values.filter((value) =>
      variants.some(
        (v) =>
          v[`option${pos}`] === value &&
          (this.currentOptions || []).every(
            (o) =>
              o.position >= pos ||
              this.selected[o.position - 1] == null ||
              v[`option${o.position}`] === this.selected[o.position - 1]
          )
      )
    );
  }

  /**
   * After an axis changes, later axes may hold a value that no longer exists
   * (e.g. Single -> Queen leaves Size on a Single-only dimension). Snap each later
   * axis to its first applicable value, preferring one that is actually in stock.
   */
  reconcileLaterAxes(changedPosition) {
    for (const o of this.currentOptions || []) {
      if (o.position <= changedPosition) continue;
      const valid = this.applicableValues(o);
      if (valid.includes(this.selected[o.position - 1])) continue;
      const inStock = valid.find((value) =>
        (this.currentProduct?.variants || []).some(
          (v) => v.available && v[`option${o.position}`] === value
        )
      );
      this.selected[o.position - 1] = inStock || valid[0] || null;
    }
  }

  renderOptionRow(opt) {
    const idx = opt.position - 1;
    const selectedValue = this.selected[idx];
    // Inapplicable values are omitted, not struck through — the PDP shows only the
    // sizes that exist for the chosen category. Strikethrough is reserved for
    // combinations that DO exist but are sold out (handled in renderOptionValue).
    const renderer = this.rendererFor(opt);
    const asCards = renderer === "card";
    const asGroup = renderer === "group";
    const asSwatch = renderer === "swatch";
    const values = this.applicableValues(opt);
    // Every renderer emits the PDP snippet's own class names and sits inside that
    // snippet's HOST class. The host is where the picker's {% stylesheet %} block
    // declares its custom properties (--f-border, --f-sel-bg, --f-green, …), so
    // without it every var silently falls back to its default. Because that CSS is
    // compiled into the shared styles.css that every page already loads, matching
    // the PDP costs nothing and cannot drift — restating values in our own file is
    // exactly what let the Cooling badge diverge.
    const HOST = {
      card: "sai-fcbfrd02",
      group: "sai-ftyfrd01",
      swatch: "sai-fswfrd01",
      size: "sai-fszfrd01",
    };
    const host = HOST[renderer] || HOST.size;

    let body;
    if (asCards) {
      body = `<div class="sai-fcbfrd02__rows">${values
        .map((v) => this.renderCardValue(opt, v, selectedValue))
        .join("")}</div>`;
    } else if (asGroup) {
      // data-fit switches ftyfrd01 between a 2-column grid of full-width cards and
      // a content-width wrap. Copied verbatim from the snippet (`values.length > 2
      // ? 'content' : 'grid'`) — it is DATA-driven, not a Studio prop. Omitting it
      // left every group axis on the grid default, so topper's 4 categories drew as
      // four half-width blocks where the PDP shows four compact pills.
      const fit = values.length > 2 ? "content" : "grid";
      body = `<div class="sai-ftyfrd01__cards" data-fit="${fit}">${values
        .map((v) => this.renderGroupValue(opt, v, selectedValue))
        .join("")}</div>`;
    } else if (asSwatch) {
      // shape/align ARE Studio props, so they get the snippet's own fallbacks
      // ('square' / 'start'). Confirmed against 12 live PDPs: no instance deviates.
      body = `<div class="sai-fswfrd01__row" data-shape="square">${values
        .map((v) => this.renderOptionValue(opt, v, true, selectedValue))
        .join("")}</div>`;
    } else {
      body = this.renderSizeBody(opt, values, selectedValue);
    }

    // ftyfrd01 alone nests one more level: __inner (gap 24px, separating multiple
    // axes in one instance) > __axis (gap 16px, label to cards). Rendering the label
    // straight into __inner picked up 24px where the PDP shows 16px. The other three
    // snippets put label and body directly in __inner, which is already 16px.
    const axisOpen = asGroup ? `<div class="sai-ftyfrd01__axis">` : "";
    const axisClose = asGroup ? `</div>` : "";
    return `
      <div class="spectrum-qa-option ${host}">
        <div class="${host}__inner"${asSwatch ? ' data-align="start"' : ""}>
          ${axisOpen}
          ${this.renderOptionHead(opt, selectedValue, host, renderer)}
          ${body}
          ${axisClose}
        </div>
      </div>
    `;
  }

  /**
   * Header row: "NAME : selected value", plus the size axis's unit toggle and
   * gender switch. Layout follows fszfrd01's three cases — units present (label on
   * its own line, then a toggle row), gender only (label and switch share the row),
   * or neither (bare label, no divider).
   */
  renderOptionHead(opt, selectedValue, host, renderer) {
    const label =
      `<div class="${host}__label">` +
      `<span class="${host}__label-key">${escapeHtml(opt.name)} :</span>` +
      `<span class="${host}__label-val">${escapeHtml(selectedValue || "")}</span>` +
      `</div>`;
    if (renderer !== "size") return label;

    const layout = this.sizeLayout(opt);
    const units = layout === "dimensions" ? this.renderUnitToggle() : "";
    const genderOpt = this.hostedGenderAxis(
      (this.currentOptions || []).filter((o) => !isDefaultTitleOption(o))
    );
    const gender = genderOpt ? this.renderGenderSwitch(genderOpt) : "";
    if (!units && !gender) return label;
    // Inline, matching fszfrd01's own `barStyle` — the snippet keeps it inline so a
    // layout change isn't held back by the theme's compiled-CSS cache.
    const bar =
      "display:flex;align-items:center;justify-content:space-between;gap:16px;" +
      "border-bottom:0.5px solid var(--f-border);padding-bottom:8px";
    return units
      ? `${label}<div class="sai-fszfrd01__toggles" style="${bar}">${units}${gender}</div>`
      : `<div class="sai-fszfrd01__toggles" style="${bar}">${label}${gender}</div>`;
  }

  /** Size values: `__pills` for plain values, `__grid` of cards for convertible ones. */
  renderSizeBody(opt, values, selectedValue) {
    const layout = this.sizeLayout(opt);
    const btn = (v, cls, label) => {
      const available = this.valueAvailable(opt, v);
      return `<button type="button" class="${cls}${v === selectedValue ? " is-sel" : ""}${
        available ? "" : " is-oos"
      }" data-option-position="${opt.position}" data-option-value="${escapeHtml(v)}"${
        available ? "" : ' aria-disabled="true"'
      }>${label}</button>`;
    };
    if (layout === "pills") {
      return `<div class="sai-fszfrd01__pills">${values
        .map((v) => btn(v, "sai-fszfrd01__pill", this.sizeLabelHtml(v)))
        .join("")}</div>`;
    }
    return `<div class="sai-fszfrd01__grid" data-cols="2" data-cols-d="3">${values
      .map((v) => btn(v, "sai-fszfrd01__card", this.sizeLabelHtml(this.sizeDisplayLabel(v, layout))))
      .join("")}</div>`;
  }

  /** Inches / Feet / CM buttons, shown only for dimension sizes (fszfrd01 `_showToggle`). */
  renderUnitToggle() {
    const unit = this.sizeUnit || DEFAULT_SIZE_UNIT;
    return (
      '<div class="sai-fszfrd01__units">' +
      UNIT_LABELS.map(
        ([u, lbl]) =>
          `<button type="button" class="sai-fszfrd01__unit" data-qa-unit="${u}" data-active="${
            u === unit
          }">${lbl}</button>`
      ).join("") +
      "</div>"
    );
  }

  /**
   * Card-style axis — the full-width rows with a thumbnail, name and
   * SAVE / MRP / price that the PDP renders with `fcbfrd02`.
   *
   * The PDP binds this through a Studio `axis` prop. Rather than copy that into a
   * per-template map (the exact thing that made the borrowed picker go stale), the
   * native picker infers it from data. All three conditions are required:
   *   - not colour / size / gender — each of those has its own renderer
   *   - the values carry authored images, i.e. there is something to put on a card
   *   - price actually varies across values, or SAVE / MRP would be dead weight
   *
   * That selects exactly the axes the PDP gives to fcbfrd02: frido-topper
   * "Cover Options", frido-maternity "Bundle", frido-nasal "Pack".
   */
  isCardOption(opt) {
    return !!opt && isCardAxisName(opt.name) && this.applicableValues(opt).length >= 1;
  }

  /** Whether this axis's values carry authored swatch images (value_display). */
  hasAuthoredImages(opt) {
    return this.applicableValues(opt).some((v) => this.displayFor(v)?.image);
  }

  /**
   * Is there an in-stock variant for `value`, given the axes chosen BEFORE this
   * one? Same scope as applicableValues() — see the note there on why checking
   * every axis instead lets a later selection strike out an earlier one.
   */
  valueAvailable(opt, value) {
    return (this.currentProduct?.variants || []).some(
      (v) =>
        v.available &&
        v[`option${opt.position}`] === value &&
        (this.currentOptions || []).every(
          (o) =>
            o.position >= opt.position ||
            this.selected[o.position - 1] == null ||
            v[`option${o.position}`] === this.selected[o.position - 1]
        )
    );
  }

  /** "dimensions" | "uk" | "pills" — drives both the layout and the unit toggle. */
  sizeLayout(opt) {
    return detectSizeLayout(this.applicableValues(opt));
  }

  sizeDisplayLabel(value, layout) {
    const unit = this.sizeUnit || DEFAULT_SIZE_UNIT;
    if (layout === "dimensions") return convertDim(value, unit);
    if (layout === "uk") return convertUk(value, unit);
    return value;
  }

  /**
   * Splits "Small (3-5 UK)" into name + unit spans, copied from fszfrd01's
   * `_labelHtml`. The split exists so the sold-out strikethrough lands on the size
   * NAME and not on the parenthesised conversion.
   */
  sizeLabelHtml(label) {
    const s = String(label);
    const i = s.indexOf(" (");
    if (i > -1 && s.endsWith(")")) {
      return `<span class="sai-fszfrd01__nm">${escapeHtml(
        s.slice(0, i)
      )}</span><span class="sai-fszfrd01__uc">${escapeHtml(s.slice(i))}</span>`;
    }
    return `<span class="sai-fszfrd01__nm">${escapeHtml(s)}</span>`;
  }

  /** Switch the size unit and repaint — labels only, selection is untouched. */
  setSizeUnit(unit) {
    if (!unit || unit === (this.sizeUnit || DEFAULT_SIZE_UNIT)) return;
    this.sizeUnit = unit;
    this.renderOptions();
  }

  /**
   * Renderer for an axis: "size" | "swatch" | "card" | "group".
   *
   * The measured AXIS_RENDERERS map is authoritative. The heuristics below only
   * run for an axis the sweep never saw (a new product template, or a picker
   * repointed in Studio since) — they are a safe default, not a parity guarantee.
   */
  rendererFor(opt) {
    const name = String(opt?.name || "").trim().toLowerCase();
    const mapped = AXIS_RENDERERS[`${this.currentTemplate || ""}||${name}`];
    if (mapped) return mapped;
    if (isColorOption(name)) return "swatch";
    if (isSizeOption(name)) return "size";
    if (isCardAxisName(name)) return "card";
    if (isGroupOption(name)) return "group";
    if (this.hasAuthoredImages(opt)) return "swatch";
    return "size";
  }

  /**
   * Price + compare-at to show on a card row.
   *
   * Cross-axis: the variant with THIS value and the currently selected other axes.
   * The min across sibling combinations is only the "from" price and would freeze
   * the row when Size changes — fcbfrd02 carries the same warning. Falls back to
   * the cheapest matching variant when no full combination resolves yet.
   */
  priceForValue(position, value) {
    const variants = this.currentProduct?.variants || [];
    const key = `option${position}`;
    const match = variants.find(
      (v) =>
        v[key] === value &&
        (this.currentOptions || []).every(
          (o) =>
            o.position === position ||
            this.selected[o.position - 1] == null ||
            v[`option${o.position}`] === this.selected[o.position - 1]
        )
    );
    const chosen =
      match ||
      variants
        .filter((v) => v[key] === value)
        .reduce((m, v) => (m == null || v.price < m.price ? v : m), null);
    if (!chosen) return null;
    const cap = chosen.compare_at_price;
    return {
      price: chosen.price,
      mrp: cap && cap > chosen.price ? cap : null,
      save: cap && cap > chosen.price ? cap - chosen.price : null,
    };
  }

  /**
   * Product-group value — a plain wide card in a 2-column grid, no image, no price
   * (the PDP's ftyfrd01 `__card`). Deliberately does NOT show the authored image:
   * maternity's Variant values have one, but the PDP's grid omits it.
   */
  renderGroupValue(opt, value, selectedValue) {
    const available = this.valueAvailable(opt, value);
    return `<button type="button" class="sai-ftyfrd01__card${
      value === selectedValue ? " is-sel" : ""
    }${available ? "" : " is-oos"}" data-option-position="${
      opt.position
    }" data-option-value="${escapeHtml(value)}"${
      available ? "" : ' aria-disabled="true"'
    }>${escapeHtml(value)}</button>`;
  }

  /**
   * Authored merchandising chip for an option value (e.g. "❄ Cooling").
   *
   * Shared by the card rows and the colour swatches — the PDP badges BOTH, so
   * wiring it only into cards left the wedge's cooling colour unmarked.
   * Colours/icon are authored per value, so they can only be applied inline.
   */
  renderBadge(value, pillClass, wrapClass) {
    const badges = this.displayFor(value)?.badges;
    const list = (Array.isArray(badges) ? badges : []).filter((b) => b && b.label);
    if (!list.length) return "";
    // The two snippets style their pill differently: fswfrd01 uses a CSS class
    // (tile-width, 8px, ellipsis) and inlines only the authored colours, while
    // fcbfrd02's badgePill() inlines the lot. Passing an empty pillClass selects
    // the fcbfrd02 shape, so each matches its own PDP renderer.
    const base = pillClass
      ? ""
      : "display:inline-flex;align-items:center;gap:3px;padding:4px 8px;" +
        "border-radius:6px;font-size:11px;font-weight:500;line-height:1;white-space:nowrap;";
    const pills = list
      .map((b) => {
        const icon = b.icon
          ? /^(https?:)?\/\//.test(b.icon)
            ? `<img src="${escapeHtml(b.icon)}" alt="" decoding="async"${
                pillClass ? "" : ' style="width:12px;height:12px;object-fit:contain;display:block"'
              }>`
            : `<span aria-hidden="true">${escapeHtml(b.icon)}</span>`
          : "";
        return `<span${pillClass ? ` class="${pillClass}"` : ""} style="${base}background:${escapeHtml(
          b.bg || "#000000"
        )};color:${escapeHtml(b.fg || "#ffffff")}">${icon}${escapeHtml(b.label)}</span>`;
      })
      .join("");
    return wrapClass ? `<span class="${wrapClass}">${pills}</span>` : pills;
  }

  /** One card row — thumb, badge, name, SAVE / MRP / price. */
  renderCardValue(opt, value, selectedValue) {
    const disp = this.displayFor(value) || {};
    const money = this.priceForValue(opt.position, value);
    const currency = this.currentProduct?.variants?.[0]?.currency || undefined;
    const available = this.valueAvailable(opt, value);
    // fcbfrd02 puts the chip in NORMAL FLOW above the row (its badgesHtml() wrapper
    // is position: relative with margin-bottom: -10px), not absolutely positioned —
    // which is why it can never be clipped by an ancestor's overflow. Both the
    // wrapper and pill styles are inline in the snippet, so they are inline here too.
    const badgePills = this.renderBadge(value, "");
    const badgeHtml = badgePills
      ? `<div style="align-self:flex-start;margin-left:12px;margin-bottom:-10px;z-index:2;position:relative;display:inline-flex;gap:4px">${badgePills}</div>`
      : "";
    const thumb = disp.image || this.variantImageForOption(opt.position, value);
    const save = money?.save
      ? `<span class="sai-fcbfrd02__save">SAVE ${formatMoney(money.save, currency)}</span>`
      : "";
    // <s> inside __mrp, not a text-decoration on the span: fcbfrd02 styles
    // `.sai-fcbfrd02__mrp s`, so the strike is lost without the element.
    const mrp = money?.mrp
      ? `<span class="sai-fcbfrd02__mrp">MRP <s>${formatMoney(money.mrp, currency)}</s></span>`
      : "";
    const price =
      money?.price != null
        ? `<span class="sai-fcbfrd02__price">${formatMoney(money.price, currency)}</span>`
        : "";
    return `
      <div class="sai-fcbfrd02__unit">
        ${badgeHtml}
        <button type="button" class="sai-fcbfrd02__row${
          value === selectedValue ? " is-sel" : ""
        }" data-option-position="${opt.position}" data-option-value="${escapeHtml(value)}"${
      available ? "" : ' aria-disabled="true"'
    }>
          <span class="sai-fcbfrd02__row-left">
            ${
              thumb
                ? `<img class="sai-fcbfrd02__row-thumb" src="${escapeHtml(
                    thumb
                  )}" alt="" decoding="async">`
                : ""
            }
            <span class="sai-fcbfrd02__rowtitle">
              <span class="sai-fcbfrd02__title${
                thumb ? " sai-fcbfrd02__title--sm" : ""
              }">${escapeHtml(value)}</span>
              ${
                disp.subtitle
                  ? `<span class="sai-fcbfrd02__subtitle"><span class="sai-fcbfrd02__sub-main">${escapeHtml(
                      disp.subtitle
                    )}</span></span>`
                  : ""
              }
            </span>
          </span>
          <span class="sai-fcbfrd02__row-right">${save}<span class="sai-fcbfrd02__prices">${mrp}${price}</span></span>
        </button>
      </div>
    `;
  }

  /** Men/Women switch, markup and behaviour copied from fszfrd01's `gender` block. */
  renderGenderSwitch(opt) {
    const [a, b] = opt.values;
    const current = this.selected[opt.position - 1];
    const lbl = (value) =>
      `<span class="sai-fszfrd01__gender-lbl" data-qa-gender="${escapeHtml(
        value
      )}" data-active="${current === value}">${escapeHtml(value)}</span>`;
    return `
      <div class="sai-fszfrd01__gender">
        ${lbl(a)}
        <button type="button" class="sai-fszfrd01__switch"
          data-qa-gender-toggle data-pos="${current === b ? "right" : "left"}"
          aria-label="Toggle ${escapeHtml(opt.name)}"></button>
        ${lbl(b)}
      </div>
    `;
  }

  /** Select `value` on the gender axis and re-resolve the axes it constrains. */
  setGender(value) {
    const opts = (this.currentOptions || []).filter((o) => !isDefaultTitleOption(o));
    const opt = this.hostedGenderAxis(opts);
    if (!opt || !value || this.selected[opt.position - 1] === value) return;
    this.selected[opt.position - 1] = value;
    // From 0, not from gender's own position: on barefoot gender precedes size,
    // but nothing guarantees that ordering, and a gender change must re-resolve
    // the size axis either way. Axes that are still valid are left untouched.
    this.reconcileLaterAxes(0);
    this.renderOptions();
    this.renderFooter();
    this.updateThumb();
  }

  /**
   * Featured-image URL of the first variant matching this option value, sized
   * small for a swatch thumbnail. Returns null when the variant has no image
   * (caller then falls back to a colour dot). `/products/{handle}.js` exposes
   * `featured_image` as an object with `.src` (or occasionally a bare string).
   */
  variantImageForOption(position, value) {
    const variant = (this.currentProduct?.variants || []).find(
      (v) => v[`option${position}`] === value
    );
    if (!variant) return null;
    const src =
      variant.featured_image?.src ||
      (typeof variant.featured_image === "string" ? variant.featured_image : null);
    if (!src) return null;
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}width=120`;
  }

  renderOptionValue(opt, value, isColor, selectedValue) {
    const selected = value === selectedValue;
    const pos = opt.position;
    // Sold-out is judged against the axes chosen BEFORE this one, the same scope
    // applicableValues() uses. Testing every axis instead let a later selection
    // strike out an earlier one: on frido-topper the chosen Size existed only for
    // Category "Single", so Double/Queen/King all rendered struck through. The PDP
    // disables none of the four.
    //
    // Picking a struck value still selects it (see the click handler) so the footer
    // can show Sold out — again matching the PDP.
    const available = (this.currentProduct.variants || []).some(
      (v) =>
        v.available &&
        v[`option${pos}`] === value &&
        (this.currentOptions || []).every(
          (o) =>
            o.position >= pos ||
            this.selected[o.position - 1] == null ||
            v[`option${o.position}`] === this.selected[o.position - 1]
        )
    );
    const ariaPressed = selected ? "true" : "false";
    const dataAttrs = `data-option-position="${opt.position}" data-option-value="${escapeHtml(
      value
    )}"`;
    const disabledAttr = available ? "" : 'aria-disabled="true"';
    if (isColor) {
      // Prefer the native option-value swatch image (identical to the PDP swatch);
      // fall back to the variant's product photo, then a flat colour dot.
      const imgUrl =
        (this.swatchMap && this.swatchMap[value]) ||
        // Authored swatch from spectrum.value_display — the same metafield the PDP's
        // colour picker renders from. Previously only PLP-card triggers supplied a
        // swatchMap, so programmatic opens fell straight through to the variant photo
        // (or a blank box when the variant had none).
        this.displayFor(value)?.image ||
        this.variantImageForOption(opt.position, value);
      const inner = imgUrl
        ? `<img src="${escapeHtml(
            imgUrl
          )}" alt="" decoding="async" width="50" height="50">`
        : `<span class="spectrum-qa-swatch__dot" style="--swatch: ${COLOR_HEX(
            value
          )};"></span>`;
      // PDP class names, so the styling comes from the picker's own {% stylesheet %}
      // block — already compiled into the shared styles.css that every page loads.
      // Copying values into our own CSS is what let the badge drift: the PDP pins it
      // to the tile's width (inset-inline: 1px, 8px type, ellipsis) inside a
      // positioned __cell, which a hand-rolled absolute pill did not reproduce.
      return `
        <span class="sai-fswfrd01__cell">
          ${this.renderBadge(value, "sai-fswfrd01__badge", "sai-fswfrd01__badges")}
          <button type="button"
            class="sai-fswfrd01__sw${selected ? " is-sel" : ""}${
        available ? "" : " is-oos"
      }"
            ${dataAttrs}
            ${disabledAttr}
            aria-pressed="${ariaPressed}"
            aria-label="${escapeHtml(value)}"
            title="${escapeHtml(value)}">
            ${inner}
          </button>
        </span>`;
    }
    return `
      <button type="button"
        class="spectrum-qa-chip${selected ? " is-selected" : ""}"
        ${dataAttrs}
        ${disabledAttr}
        aria-pressed="${ariaPressed}">
        ${escapeHtml(value)}
      </button>`;
  }

  /** Footer CTA label for a variant: pre-order vs default. */
  atcLabelFor(variant) {
    if (variant && this.preorderIds && this.preorderIds.has(String(variant.id))) {
      return this.preorderLabel || "Pre-Order Now";
    }
    return "Add to Cart";
  }

  resolveVariant() {
    if (!this.currentProduct) return null;
    if (this.selected.some((v) => v == null)) return null;
    return (this.currentProduct.variants || []).find((v) =>
      this.selected.every((sel, i) => v[`option${i + 1}`] === sel)
    );
  }

  renderFooter() {
    const variant = this.resolveVariant();
    const currency = this.currentProduct?.price_currency || undefined;

    if (!variant) {
      this.priceEls.forEach((el) => {
        el.innerHTML =
          '<span class="spectrum-qa-dialog__unavailable">Select options to continue</span>';
      });
      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = "Add to Cart";
      }
      return;
    }

    const price = variant.price;
    const compare = variant.compare_at_price || 0;
    const onSale = compare > price;
    const pct = onSale ? Math.round(((compare - price) * 100) / compare) : 0;

    const priceHTML = `
        <span class="spectrum-qa-dialog__price-now">${formatMoney(price, currency)}</span>
        ${
          onSale
            ? `<span class="spectrum-qa-dialog__price-mrp">${formatMoney(
                compare,
                currency
              )}</span>${
                pct > 0
                  ? `<span class="spectrum-qa-dialog__discount">${pct}% OFF</span>`
                  : ""
              }`
            : ""
        }
      `;
    this.priceEls.forEach((el) => { el.innerHTML = priceHTML; });

    if (this.atcBtn) {
      if (!variant.available) {
        this.atcBtn.disabled = true;
        this.atcBtn.classList.remove("is-adding");
        this.atcBtn.textContent = "Sold Out";
      } else if (this.atcInFlight) {
        // Spinner only — the "Adding…" label used to sit under the spinner and
        // was still on screen as the sheet slid away.
        this.atcBtn.disabled = true;
        this.atcBtn.classList.add("is-adding");
        this.atcBtn.innerHTML =
          '<span class="spectrum-qa-dialog__spinner" aria-hidden="true"></span>' +
          '<span class="spectrum-qa-dialog__sr">Adding to cart</span>';
      } else {
        this.atcBtn.disabled = false;
        this.atcBtn.classList.remove("is-adding");
        // "Pre-Order Now" for an available pre-order variant, else "Add to Cart" —
        // matching the PDP CTA and the PLP card. Only reached in the available
        // branch, so an OOS pre-order variant still reads "Sold Out" above.
        this.atcBtn.textContent = this.atcLabelFor(variant);
      }
    }
  }

  async modalAtc() {
    const variant = this.resolveVariant();
    if (!variant || !variant.available || this.atcInFlight) return;
    this.atcInFlight = true;
    this.renderFooter();
    // Tell spectrum-cart-sync.js to stay out of the way: the mini-PDP performs
    // the add AND dispatches its own cart:update with rendered section HTML, so
    // the drawer opens already populated. Without this, the sync shim fires a
    // sections-less cart:update first and opens the drawer on the empty state.
    window.__spectrumQuickAddInFlight = true;
    try {
      const { cart, sections } = await this.addToCart(variant.id);
      // Passing `sections` lets the theme's cart-items-component morph straight
      // to the filled state inside the cart:update dispatch (instead of kicking
      // off a slow async section re-render). Drawer open + fill happen in the
      // same synchronous event tick, so the drawer is already populated when it
      // appears — no empty-cart flash.
      this.dispatchCartUpdate(cart, "spectrum-quick-add", {
        variantId: String(variant.id),
        productId: String(this.currentProduct?.id || ""),
        sections,
      });
      this.close();
    } catch (err) {
      console.error("[spectrum-quick-add] ATC failed", err);
      this.dispatchCartError(err);
    } finally {
      this.atcInFlight = false;
      this.renderFooter();
      window.__spectrumQuickAddInFlight = false;
    }
  }

  async directAtc(btn, variantId, quantity = 1) {
    if (btn.dataset.spectrumAtcInFlight === "true") return;
    btn.dataset.spectrumAtcInFlight = "true";
    // innerHTML, not textContent: the in-flight state swaps in a spinner element, so the
    // label has to be restored as markup for the button to come back intact.
    const original = btn.innerHTML;
    btn.disabled = true;
    // A spinner rather than an "Adding…" label — the buttons are narrow enough that the
    // word reflows the tile, and the spinner reads at a glance. The text equivalent stays
    // for assistive tech, which cannot see the spinner.
    btn.innerHTML =
      '<span class="spectrum-atc-spinner" aria-hidden="true"></span>' +
      '<span class="spectrum-qa-dialog__sr">Adding to cart</span>';
    // See modalAtc — suppress the sync shim so the drawer opens only once the
    // add has completed and its section HTML is morphed in.
    window.__spectrumQuickAddInFlight = true;
    try {
      const { cart, sections } = await this.addToCart(variantId, quantity);
      this.dispatchCartUpdate(cart, "spectrum-quick-add-direct", {
        variantId: String(variantId),
        sections,
      });
      btn.innerHTML = original;
    } catch (err) {
      console.error("[spectrum-quick-add] direct ATC failed", err);
      this.dispatchCartError(err);
      btn.textContent = "Try again";
    } finally {
      btn.dataset.spectrumAtcInFlight = "false";
      btn.disabled = false;
      window.__spectrumQuickAddInFlight = false;
      setTimeout(() => {
        if (btn.textContent === "Try again") btn.innerHTML = original;
      }, 2500);
    }
  }

  /**
   * Section IDs the theme's cart UI needs re-rendered after an add. Read live
   * from the `<cart-items-component data-section-id>` element(s) so this stays
   * theme-agnostic — Horizon nests the drawer in the header section, other
   * themes use a dedicated cart-drawer section id. Capped at Shopify's max of 5.
   */
  cartSectionIds() {
    const ids = new Set();
    document
      .querySelectorAll("cart-items-component[data-section-id]")
      .forEach((el) => {
        if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
      });
    return [...ids].slice(0, 5);
  }

  async addToCart(variantId, quantity = 1) {
    // Request the cart section HTML alongside the add so the drawer can be
    // morphed to its filled state synchronously (no empty-cart flash). Falls
    // back gracefully to a plain add when no cart sections are present.
    const sectionIds = this.cartSectionIds();
    const body = { id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) };
    if (sectionIds.length) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: "Add to cart failed" }));
      throw err;
    }
    // The add response carries the rendered `sections` HTML (when requested);
    // the /cart.js fetch gives the full cart object for the event resource +
    // cart bubble count.
    const addJson = await res.json().catch(() => ({}));
    const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    const cart = cartRes.ok ? await cartRes.json() : null;
    return { cart, sections: addJson?.sections || null };
  }

  dispatchCartUpdate(cart, sourceId, data) {
    const evt = new Event(CART_UPDATE_EVENT, { bubbles: true });
    evt.detail = {
      resource: cart || {},
      sourceId,
      data: { source: sourceId, ...data },
    };
    document.dispatchEvent(evt);
  }

  dispatchCartError(err) {
    const evt = new Event(CART_ERROR_EVENT, { bubbles: true });
    evt.detail = { resource: {}, data: { error: err, source: "spectrum-quick-add" } };
    document.dispatchEvent(evt);
  }
}

// Expose the singleton so other surfaces (e.g. the search-results bridge) can
// open the same mini-PDP programmatically: window.spectrumQuickAdd.open(handle).
function initSpectrumQuickAdd() {
  if (!window.spectrumQuickAdd) window.spectrumQuickAdd = new SpectrumQuickAdd();
  return window.spectrumQuickAdd;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSpectrumQuickAdd);
} else {
  initSpectrumQuickAdd();
}
